import { ChangeDetectionStrategy, Component, OnInit } from "@angular/core";
import { IdentityService } from "@common/identity/identity.service";
import { IMcpOAuthAuthorization } from "@common/identity/mcp-oauth-authorization.interface";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { from } from "rxjs";
import { catchError, switchMap, tap } from "rxjs/operators";

@Component({
    selector: "adapt-configure-personal-mcp-oauth-authorizations",
    templateUrl: "./configure-personal-mcp-oauth-authorizations.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ConfigurePersonalMcpOAuthAuthorizationsComponent implements OnInit {
    public authorizations?: IMcpOAuthAuthorization[];
    public errorMessage?: string;

    public constructor(
        private identityService: IdentityService,
        private dialogService: AdaptCommonDialogService,
    ) { }

    public async ngOnInit() {
        await this.loadAuthorizations();
    }

    @Autobind
    public revokeAuthorization(authorization: IMcpOAuthAuthorization) {
        return this.dialogService.openConfirmationDialog({
            title: "Revoke application access",
            message: `<p>This application will no longer be able to access Lumia on your behalf.</p>
                      <p>You will need to authorise it again before reconnecting.</p>`,
            confirmButtonText: "Revoke access",
            confirmButtonPreset: "delete",
        }).pipe(
            switchMap(() => from(this.identityService.revokeMcpOAuthAuthorization(authorization.ClientId))),
            tap(() => this.authorizations = this.authorizations?.filter((item) => item.ClientId !== authorization.ClientId)),
            catchError((error) => this.dialogService.showErrorDialog(
                "Failed to revoke access",
                ErrorHandlingUtilities.getHttpResponseMessage(error),
            )),
        );
    }

    private async loadAuthorizations() {
        try {
            const response = await this.identityService.getMcpOAuthAuthorizations();
            this.authorizations = response.body ?? [];
            this.errorMessage = undefined;
        } catch (error) {
            this.authorizations = [];
            this.errorMessage = ErrorHandlingUtilities.getHttpResponseMessage(error);
        }
    }
}
